/**
 * SuggestionsPanel.tsx - Lays out both sets of suggestion chips on the empty state
 *
 * The personalized chips and the admin-configured ones are fetched by two independent requests
 * that finish at very different times: the admin post is a single read, the personalized set can
 * cost a model call the server budgets ten seconds for.
 *
 * This panel used to hold BOTH behind one skeleton until both had settled, so that neither moved
 * the layout as it landed. That traded a real cost for a cosmetic one — the admin chips were
 * ready in milliseconds and sat hidden behind a slow model call, so the common case was a screen
 * of grey bars with nothing happening. The fast set no longer waits on the slow one.
 *
 * What replaces the gate:
 *   - the admin chips are held only by their OWN request, which is short
 *   - the personalized slot shows a pending pill instead, so the space is occupied and the swap
 *     costs about one row rather than re-flowing the whole column
 *   - the pill appears only if the wait is long enough to notice, so a pre-warmed or
 *     immediately-empty response never flashes it
 *
 * Most of the time there is no wait to show at all: usePrewarmPersonalizedQuestions generates
 * the set after the previous answer lands, so opening a new chat is a cache hit.
 *
 * Both hooks are called again inside the chip components themselves — SWR keys by URL, so those
 * are cache reads, not second requests.
 */

import { useEffect, useState } from 'react';
import { Flex, Skeleton, VStack } from '@chakra-ui/react';
import { useChatContext } from '@/contexts/ChatContext';
import { usePersonalizedQuestions } from '@/hooks/usePersonalizedQuestions';
import { useSuggestedQuestions } from '@/hooks/useSuggestedQuestions';
import { PersonalizedQuestionChips } from './PersonalizedQuestionChips';
import { PersonalizedQuestionsPending } from './PersonalizedQuestionsPending';
import { SuggestedQuestionChips } from './SuggestedQuestionsWidget';

/**
 * How long a personalized request may run before we admit to it.
 *
 * The two fast paths — a set already pre-warmed into the SWR cache, and a brand-new user the
 * server answers immediately because they have no history — both resolve well inside this, and
 * showing a pill that vanishes again is worse than showing nothing at all.
 */
export const PENDING_VISIBLE_AFTER_MS = 600;

/** Chip-shaped placeholders for the admin row, sized so the swap costs no layout shift. */
const ADMIN_CHIP_WIDTHS = ['9rem', '14rem', '10rem'];

const AdminChipSkeleton: React.FC = () => (
    <Flex
        width="100%"
        wrap="wrap"
        justifyContent="center"
        gap={{ base: 1.5, md: 2 }}
        aria-hidden="true"
    >
        {ADMIN_CHIP_WIDTHS.map((width, i) => (
            <Skeleton
                key={i}
                width={width}
                maxWidth="100%"
                height={{ base: '2.25rem', md: '2.6rem' }}
                borderRadius="var(--cair-radius-lg)"
            />
        ))}
    </Flex>
);

/** True once `active` has been continuously true for `delayMs`. Resets the moment it clears. */
const useSettledDelay = (active: boolean, delayMs: number): boolean => {
    const [elapsed, setElapsed] = useState(false);

    useEffect(() => {
        if (!active) {
            setElapsed(false);
            return;
        }
        const timer = setTimeout(() => setElapsed(true), delayMs);
        return () => clearTimeout(timer);
    }, [active, delayMs]);

    return active && elapsed;
};

export const SuggestionsPanel: React.FC = () => {
    const { parentCircleId } = useChatContext();
    const circleId = parentCircleId || null;

    const personalized = usePersonalizedQuestions(circleId);
    const admin = useSuggestedQuestions(circleId);

    const showPending = useSettledDelay(personalized.isLoading, PENDING_VISIBLE_AFTER_MS);

    // The admin request is a single read, so its own placeholder is short-lived by construction.
    if (admin.isLoading) {
        return <AdminChipSkeleton />;
    }

    const personalizedSlot = showPending
        ? <PersonalizedQuestionsPending />
        : personalized.questions.length
            ? <PersonalizedQuestionChips />
            : null;
    const adminSlot = admin.questions.length ? <SuggestedQuestionChips /> : null;

    // Nothing to show and nothing on the way — render nothing at all rather than leaving a gap.
    if (!personalizedSlot && !adminSlot) {
        return null;
    }

    return (
        <VStack width="100%" gap={{ base: 1.5, md: 2 }}>
            {personalizedSlot}
            {adminSlot}
        </VStack>
    );
};
